import { faBarsFilter } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    ButtonProps,
    Checkbox,
    ListItemText,
    Menu,
    MenuItemProps,
    Button as MuiButton,
    MenuItem as MuiMenuItem,
    styled,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { LanguageContext } from "src/contexts/language";
import { MapContext } from "src/contexts/map";
import { groupCategoriesByParentId } from "src/utils/category";

const MenuItem = styled(MuiMenuItem)<MenuItemProps>(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(2),
}));

const DateMenuItem = styled(MuiMenuItem)<MenuItemProps>(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    "&:hover, &.Mui-focusVisible": {
        backgroundColor: theme.palette.transparent.main,
    },
}));

const Button = styled(MuiButton)<ButtonProps>(({ theme }) => ({
    width: theme.spacing(25),
    height: theme.spacing(5),
    color: theme.palette.text.secondary,
    justifyContent: "flex-start",
    borderColor: theme.palette.secondary.main,
    "&:hover": {
        borderColor: theme.palette.common.black,
    },
}));

const ResetButton = styled(MuiButton)<ButtonProps>(({ theme }) => ({
    color: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
    "&:hover": {
        color: theme.palette.secondary.main,
        borderColor: theme.palette.secondary.main,
    },
}));

const ValidateButton = styled(MuiButton)<ButtonProps>(({ theme }) => ({
    "&:hover": {
        backgroundColor: theme.palette.primary.main,
    },
}));

const ActionsMenuItem = styled(MenuItem)<MenuItemProps>(({ theme }) => ({
    "&:hover": { backgroundColor: theme.palette.transparent.main },
}));

export type Type_Filters = {
    selectedCategories: number[];
    startDate: Dayjs | null;
    endDate: Dayjs | null;
    search?: string | null;
};

const default_filter_value: Type_Filters = {
    selectedCategories: [],
    startDate: dayjs(),
    endDate: null,
};

const FilterElements: React.FC = () => {
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const { categories, updateFilters, updateNewElement } =
        useContext(MapContext);
    const { language } = useContext(LanguageContext);

    const [filters, setFilters] = useState<Type_Filters>(default_filter_value);

    const groupedCategories = groupCategoriesByParentId(categories);

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleCategoryChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const value = parseInt(event.target.value);
        setFilters((prev) => ({
            ...prev,
            selectedCategories: prev.selectedCategories.includes(value)
                ? prev.selectedCategories.filter((id) => id !== value)
                : [...prev.selectedCategories, value],
        }));
    };

    const handleDateChange = (
        name: keyof Type_Filters,
        value: Dayjs | null,
    ) => {
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleReset = () => {
        // this updates the context filters
        // the mapView component will re-render and apply the filters when fetching elements
        // same logic is used with the mobile filters
        updateFilters(default_filter_value);
        setFilters(default_filter_value);
        handleCloseMenu();
    };

    const handleSubmit = () => {
        updateNewElement(null);
        updateFilters(filters);
        handleCloseMenu();
    };

    return (
        <div>
            <Button
                onClick={handleOpenMenu}
                variant="outlined"
                startIcon={<FontAwesomeIcon icon={faBarsFilter} />}
                disableRipple
            >
                {t("filter")}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
            >
                {groupedCategories.map((category) => (
                    <div key={category.id}>
                        <MuiMenuItem disabled>
                            <strong>
                                {category.title[language as string]}
                            </strong>
                        </MuiMenuItem>
                        {category?.children &&
                            category.children.map((item) => (
                                <MuiMenuItem
                                    disableRipple
                                    key={item.id}
                                    onClick={() =>
                                        handleCategoryChange({
                                            target: {
                                                value: item.id.toString(),
                                            },
                                        } as unknown as React.ChangeEvent<HTMLInputElement>)
                                    }
                                >
                                    <ListItemText
                                        primary={item.title[language]}
                                    />
                                    <Checkbox
                                        disableRipple
                                        checked={filters.selectedCategories.includes(
                                            item.id,
                                        )}
                                    />
                                </MuiMenuItem>
                            ))}
                    </div>
                ))}
                <MuiMenuItem disabled>
                    <strong>{t("period")}</strong>
                </MuiMenuItem>
                <DateMenuItem disableRipple>
                    <Typography color="secondary.main" variant="subtitle2">
                        {t("addElement.startDate")}
                    </Typography>
                    <DatePicker
                        value={filters.startDate}
                        onChange={(value) =>
                            handleDateChange("startDate", value)
                        }
                    />
                </DateMenuItem>
                <DateMenuItem disableRipple>
                    <Typography color="secondary.main" variant="subtitle2">
                        {t("addElement.endDate")}
                    </Typography>
                    <DatePicker
                        value={filters.endDate}
                        onChange={(value) => handleDateChange("endDate", value)}
                    />
                </DateMenuItem>

                <ActionsMenuItem disableRipple>
                    <ResetButton
                        disableRipple
                        variant="outlined"
                        onClick={handleReset}
                    >
                        {t("reset")}
                    </ResetButton>

                    <ValidateButton
                        disableRipple
                        variant="contained"
                        onClick={handleSubmit}
                    >
                        {t("validate")}
                    </ValidateButton>
                </ActionsMenuItem>
            </Menu>
        </div>
    );
};

export default FilterElements;
